import { useState } from 'react';
import { api, ApiRequestError } from '../lib/api.js';
import { useSession } from '../lib/session.js';

/**
 * Sign in.
 *
 * The only unauthenticated screen, and the only place the console is not framed
 * by the interlock strip — because posture is configuration, and an
 * unauthenticated visitor must not learn whether the bot is armed.
 *
 * v2 (docs/design_handoff_console_redesign/): the status pill on the left
 * pane is chrome-accent lime, not safed-cyan -- the one explicit exception
 * to "SAFED always means cool cyan" in the whole redesign. It reads
 * "Safed" because that is still true (nothing can move pre-session), but
 * it is chrome here, not a live instrument reading: there is no real
 * interlock strip on this screen (there is no session yet to have a
 * posture), so this badge is decorative copy, not the same signal the
 * authenticated strip gives everywhere else. Deliberately NOT built from
 * `.strip-state`/`data-armed` for that reason -- reusing that class here
 * would make a decorative badge look like a real state readout.
 */

export function SignIn() {
  const { refresh } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.post('/auth/login', { email: email.trim(), password });
      await refresh();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(
          err.status === 429
            ? 'Too many attempts. Wait a few minutes and try again.'
            : "That email and password don't match.",
        );
      } else {
        setError('Could not reach the server. Check that the API is running.');
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="signin-frame">
      <div className="signin-card">
        <aside className="signin-aside">
          <div>
            <div className="row" style={{ marginBottom: 'var(--s-10)' }}>
              <span className="signin-mark" aria-hidden="true" />
              <span className="brand-name">RH·CST — chain 4663</span>
            </div>
            <h1 className="signin-title">
              Operator
              <br />
              Console
            </h1>
          </div>

          <div className="stack-sm">
            {/* Decorative chrome, not a live interlock reading -- see this
                file's own header comment for why this is the one place
                lime replaces the SAFED cyan used everywhere else. */}
            <span
              className="row"
              style={{
                alignSelf: 'flex-start',
                gap: 'var(--s-2)',
                padding: '5px 12px',
                borderRadius: 'var(--r-pill)',
                background: 'var(--chrome-accent-tint)',
                border: '1px solid var(--chrome-accent-border)',
              }}
            >
              <span
                className="strip-dot"
                aria-hidden="true"
                style={{ background: 'var(--chrome-accent)' }}
              />
              <span className="data-xs" style={{ color: 'var(--chrome-accent)' }}>
                Safed — no session, nothing can move
              </span>
            </span>
            <p className="empty-body" style={{ margin: 0, textAlign: 'left' }}>
              Single operator. No signup, no recovery flow, no team invites — if
              you are reading this screen, it is you.
            </p>
          </div>
        </aside>

        <form className="signin-form" onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              className="input"
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="password">
              Password
            </label>
            <input
              className="input"
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="error-text" role="alert">
              {error}
            </p>
          )}

          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Authenticating…' : 'Authenticate'}
          </button>

          <p className="empty-body" style={{ margin: 0, fontSize: 'var(--text-tiny)' }}>
            Session tied to this device. No password reset by design.
          </p>
        </form>
      </div>
    </div>
  );
}
